import type { UnifiedThemeOptions } from '@backstage/theme';

/**
 * Kärcher corporate design tokens, mirroring the `--color-*` custom properties
 * used by kaercher.com. Levels 700/800 of the petrol ramp are darkened
 * extensions: Kärcher only publishes up to 600, which is too light to reach
 * WCAG AA for body text on white.
 */
export const karcher = {
  yellow: '#FFED00',
  yellowTint: '#FFF466',
  neutral: {
    0: '#FFFFFF',
    50: '#F7F7F7',
    100: '#F1F1F1',
    200: '#E2E2E2',
    300: '#D4D4D4',
    400: '#B7B7B7',
    500: '#7D7D7D',
    600: '#6E6E6E',
    700: '#5D5D5D',
    800: '#4B4B4B',
    900: '#2B2B2B',
    950: '#161616',
    1000: '#000000',
  },
  petrol: {
    100: '#BDDDE0',
    200: '#91C6CB',
    400: '#3998A1',
    500: '#238C96',
    600: '#19828C',
    700: '#16737C',
    800: '#0F6069',
  },
  status: {
    error: '#E2021A',
    success: '#49A83E',
    warning: '#F46C0B',
    info: '#006EE4',
  },
  statusBackground: {
    error: '#FCE6E8',
    success: '#EDF6EC',
    warning: '#FEF0E7',
    info: '#E5F0FC',
  },
} as const;

/**
 * Kärcher's own design system separates these two, and so do we:
 * `--color-primary` is black and drives every interactive state, while
 * `--color-primary-brand` (yellow) is a background-only accent. Yellow is
 * 1.21:1 on white, so it can never carry text, borders or state.
 *
 * Petrol stands in for black as the interactive colour. It is a first-class
 * Kärcher token (and their legacy focus ring), and unlike black it survives
 * the `darken()`/`lighten()` derivations Backstage and Material UI apply to
 * `palette.link` and `palette.primary`.
 */
const interactive = {
  light: karcher.petrol[700],
  lightHover: karcher.petrol[800],
  dark: karcher.petrol[200],
  darkHover: karcher.petrol[100],
};

/** Darkest Kärcher-adjacent grey that clears 3:1 on white, for input borders. */
export const inputBorder = '#767676';

/** Focus ring, taken from Kärcher's `--color-focus-outline`. */
export const focusRing = karcher.status.info;

export const karcherLightPalette: UnifiedThemeOptions['palette'] = {
  // `type` is read by Material UI v4 and `mode` by v5. Both are required:
  // ToolCardLogoStyles branches on `palette.type`.
  type: 'light',
  mode: 'light',
  background: {
    default: karcher.neutral[50],
    paper: karcher.neutral[0],
  },
  primary: {
    main: interactive.light,
    dark: interactive.lightHover,
    light: karcher.petrol[500],
    contrastText: karcher.neutral[0],
  },
  secondary: {
    main: karcher.neutral[900],
    dark: karcher.neutral[950],
    light: karcher.neutral[700],
    contrastText: karcher.neutral[0],
  },
  error: {
    main: karcher.status.error,
    contrastText: karcher.neutral[0],
  },
  warning: {
    main: karcher.status.warning,
    contrastText: karcher.neutral[1000],
  },
  success: {
    main: '#2E7D26',
    contrastText: karcher.neutral[0],
  },
  info: {
    main: karcher.status.info,
    contrastText: karcher.neutral[0],
  },
  text: {
    primary: karcher.neutral[950],
    secondary: karcher.neutral[700],
    disabled: karcher.neutral[400],
  },
  divider: karcher.neutral[200],
  status: {
    ok: karcher.status.success,
    warning: karcher.status.warning,
    error: karcher.status.error,
    pending: karcher.neutral[500],
    running: karcher.status.info,
    aborted: karcher.neutral[400],
  },
  border: karcher.neutral[200],
  textContrast: karcher.neutral[1000],
  textVerySubtle: karcher.neutral[400],
  textSubtle: karcher.neutral[700],
  highlight: karcher.yellowTint,
  errorBackground: karcher.statusBackground.error,
  warningBackground: karcher.statusBackground.warning,
  infoBackground: karcher.statusBackground.info,
  errorText: '#A80214',
  infoText: '#00479A',
  warningText: '#A8490B',
  link: interactive.light,
  linkHover: interactive.lightHover,
  gold: karcher.yellow,
  navigation: {
    background: karcher.neutral[950],
    indicator: karcher.yellow,
    color: karcher.neutral[400],
    selectedColor: karcher.neutral[0],
    navItem: { hoverBackground: karcher.neutral[900] },
    submenu: { background: karcher.neutral[900] },
  },
  tabbar: { indicator: karcher.yellow },
  pinSidebarButton: {
    icon: karcher.neutral[950],
    background: karcher.neutral[400],
  },
  banner: {
    info: karcher.status.info,
    error: karcher.status.error,
    warning: karcher.status.warning,
    text: karcher.neutral[0],
    link: karcher.neutral[0],
    closeButtonColor: karcher.neutral[0],
  },
  code: { background: karcher.neutral[100] },
  bursts: {
    fontColor: karcher.neutral[0],
    slackChannelText: karcher.neutral[200],
    backgroundColor: { default: karcher.neutral[900] },
    gradient: {
      linear: `linear-gradient(-137deg, ${karcher.petrol[500]} 0%, ${karcher.petrol[800]} 100%)`,
    },
  },
};

export const karcherDarkPalette: UnifiedThemeOptions['palette'] = {
  type: 'dark',
  mode: 'dark',
  // Kärcher's dark `--color-bg-container` is pure black. We lift the page to
  // neutral-950 instead: pure black halates against light text and smears on
  // OLED, which matters for a tool developers keep open all day.
  background: {
    default: karcher.neutral[950],
    paper: karcher.neutral[900],
  },
  primary: {
    main: interactive.dark,
    dark: karcher.petrol[400],
    light: interactive.darkHover,
    contrastText: karcher.neutral[950],
  },
  secondary: {
    main: karcher.neutral[200],
    dark: karcher.neutral[400],
    light: karcher.neutral[100],
    contrastText: karcher.neutral[950],
  },
  error: {
    main: '#FF6B6B',
    contrastText: karcher.neutral[950],
  },
  warning: {
    main: '#FFA42C',
    contrastText: karcher.neutral[1000],
  },
  success: {
    main: '#71CF88',
    contrastText: karcher.neutral[950],
  },
  info: {
    main: '#6FB0F8',
    contrastText: karcher.neutral[950],
  },
  text: {
    primary: karcher.neutral[50],
    secondary: '#DBDBDB',
    disabled: karcher.neutral[500],
  },
  divider: '#3B3B3B',
  status: {
    ok: '#71CF88',
    warning: '#FFA42C',
    error: '#FF6B6B',
    pending: karcher.neutral[500],
    running: '#6FB0F8',
    aborted: karcher.neutral[600],
  },
  border: '#3B3B3B',
  textContrast: karcher.neutral[0],
  textVerySubtle: karcher.neutral[600],
  textSubtle: '#DBDBDB',
  // Yellow would be on-brand here, but the highlighted run inherits
  // `text.primary`, and white on yellow is 1.21:1. A dark ochre keeps the
  // yellow association while leaving the text readable.
  highlight: '#5C5200',
  errorBackground: '#350708',
  warningBackground: '#491E00',
  infoBackground: '#052A56',
  errorText: '#FF9A9A',
  infoText: '#9CC9FF',
  warningText: '#F2AB4A',
  link: interactive.dark,
  linkHover: interactive.darkHover,
  gold: karcher.yellow,
  navigation: {
    background: karcher.neutral[1000],
    indicator: karcher.yellow,
    color: karcher.neutral[400],
    selectedColor: karcher.neutral[0],
    navItem: { hoverBackground: karcher.neutral[950] },
    submenu: { background: karcher.neutral[950] },
  },
  tabbar: { indicator: karcher.yellow },
  pinSidebarButton: {
    icon: karcher.neutral[950],
    background: karcher.neutral[400],
  },
  banner: {
    info: karcher.status.info,
    error: karcher.status.error,
    warning: karcher.status.warning,
    text: karcher.neutral[0],
    link: karcher.neutral[0],
    closeButtonColor: karcher.neutral[0],
  },
  code: { background: karcher.neutral[950] },
  bursts: {
    fontColor: karcher.neutral[0],
    slackChannelText: karcher.neutral[200],
    backgroundColor: { default: karcher.neutral[900] },
    gradient: {
      linear: `linear-gradient(-137deg, ${karcher.petrol[500]} 0%, ${karcher.petrol[800]} 100%)`,
    },
  },
};
